import {
  Component,
  OnDestroy,
  OnInit,
  HostBinding,
  HostListener,
  ViewEncapsulation,
  TemplateRef,
} from "@angular/core";
import { MediaObserver } from "@angular/flex-layout";

import * as _ from "lodash";
import { Subject, Observable } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { AuthenticationService } from "app/auth/service";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreMediaService } from "@core/services/media.service";

import { User } from "app/auth/models";

import { coreConfig } from "app/app-config";
import { Router } from "@angular/router";
import { KeycloakService } from "keycloak-angular";
import { SystemHealthService, MaintenanceWindow } from "app/api/system-health.service";
import { NavbarPortalService, PageHeaderInfo } from "./services/navbar-portal.service";

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent implements OnInit, OnDestroy {
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: User;

  // Navbar Portal Observables
  public centerPortal$: Observable<TemplateRef<any> | null>;
  public leftPortal$: Observable<TemplateRef<any> | null>;
  public rightPortal$: Observable<TemplateRef<any> | null>;
  public pageHeader$: Observable<PageHeaderInfo | null>;
  public hasRightPortal$: Observable<boolean>;

  // ── Maintenance Banner (Feature 2) ──────────────────────────────────────
  maintenanceWindows: MaintenanceWindow[] = [];
  showMaintenanceBanner: boolean = false;
  maintenanceDismissed: boolean = false;
  private _maintenanceInterval: any = null;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;

  @HostBinding("class.fixed-top")
  public isFixed = false;

  @HostBinding("class.navbar-static-style-on-scroll")
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener("window:scroll", [])
  onWindowScroll() {
    if (
      (window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == "navbar-static-top" &&
      this.coreConfig.layout.type == "horizontal"
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {MediaObserver} _mediaObserver
   * @param {TranslateService} _translateService
   * @param {NavbarPortalService} _navbarPortalService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    private _keyCloakService: KeycloakService,
    private _systemHealthService: SystemHealthService,
    public navbarPortalService: NavbarPortalService,
  ) {
    this.centerPortal$ = this.navbarPortalService.centerPortal$;
    this.leftPortal$ = this.navbarPortalService.leftPortal$;
    this.rightPortal$ = this.navbarPortalService.rightPortal$;
    this.pageHeader$ = this.navbarPortalService.pageHeader$;
    this.hasRightPortal$ = this.navbarPortalService.hasRightPortal$;
    this._authenticationService.currentUser.subscribe(
      (x) => (this.currentUser = x),
    );

    this.languageOptions = {
      en: {
        title: "English",
        flag: "us",
      },
      fr: {
        title: "French",
        flag: "fr",
      },
      de: {
        title: "German",
        flag: "de",
      },
      pt: {
        title: "Portuguese",
        flag: "pt",
      },
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig(
      { app: { appLanguage: language } },
      { emitEvent: true },
    );
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem("prevSkin");

    if (this.currentSkin === "dark") {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : "default" } },
        { emitEvent: true },
      );
    } else {
      localStorage.setItem("prevSkin", this.currentSkin);
      this._coreConfigService.setConfig(
        { layout: { skin: "dark" } },
        { emitEvent: true },
      );
    }
  }




  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // get the currentUser details from localStorage
    this.currentUser = JSON.parse(localStorage.getItem("currentUser"));

    // Subscribe to the config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
        this.horizontalMenu = config.layout.type === "horizontal";
        this.hiddenMenu = config.layout.menu.hidden === true;
        this.currentSkin = config.layout.skin;

        // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
        if (this.coreConfig.layout.type === "vertical") {
          setTimeout(() => {
            if (this.coreConfig.layout.navbar.type === "fixed-top") {
              this.isFixed = true;
            }
          }, 0);
        }
      });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == "horizontal") {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          const isFixedTop = this._mediaObserver.isActive("bs-gt-xl");
          if (isFixedTop) {
            this.isFixed = false;
          } else {
            this.isFixed = true;
          }
        });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang,
    });

    // ── Poll maintenance status every 30s (Feature 2) ──────────────────────
    // Tạm thời ẩn/tắt polling maintenance API theo yêu cầu
    // this._pollMaintenance();
    // this._maintenanceInterval = setInterval(() => this._pollMaintenance(), 30000);
  }

  private _pollMaintenance(): void {
    this._systemHealthService.getMaintenanceActive().subscribe({
      next: (res: any) => {
        const isSuccess = res?.success === true || res?.result === true || res?.code === 200;
        if (isSuccess) {
          let data = res.data;
          if (data === undefined && typeof res.message === 'string' && (res.message.trim().startsWith('{') || res.message.trim().startsWith('['))) {
            try {
              data = JSON.parse(res.message);
            } catch (e) {
              console.warn('Failed to parse maintenance active message JSON:', e);
            }
          }
          this.maintenanceWindows = Array.isArray(data) ? data : (data ? [data] : []);
          // Auto-show banner when maintenance is active (only if not manually dismissed)
          if (this.maintenanceWindows.length > 0) {
            this.maintenanceDismissed = false;
            this.showMaintenanceBanner = true;
          } else {
            // Hide automatically when maintenance ends
            this.showMaintenanceBanner = false;
            this.maintenanceDismissed = false;
          }
        }
      },
      error: (err) => {
        // Silently ignore — not critical
        console.debug('Maintenance poll failed (non-critical):', err?.status);
      },
    });
  }

  dismissMaintenanceBanner(): void {
    this.showMaintenanceBanner = false;
    this.maintenanceDismissed = true;
  }
  logout() {
    this._authenticationService.logout();
  }
  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    if (this._maintenanceInterval) {
      clearInterval(this._maintenanceInterval);
      this._maintenanceInterval = null;
    }
  }
}
